import React, { useMemo } from "react";
import "./Pagination.css";

function buildPageItems(page, totalPages, windowSize) {
  const safeTotal = Math.max(1, Number(totalPages) || 1);
  const safePage = Math.min(safeTotal, Math.max(1, Number(page) || 1));
  const size = Math.max(1, Number(windowSize) || 8);

  const maxStart = Math.max(1, safeTotal - size + 1);
  const start = Math.min(
    maxStart,
    Math.max(1, safePage - Math.floor(size / 2))
  );
  const end = Math.min(safeTotal, start + size - 1);

  const items = [];

  if (start > 1) {
    items.push({ type: "ellipsis", key: "start-ellipsis" });
  }

  for (let n = start; n <= end; n += 1) {
    items.push({ type: "page", value: n, key: `page-${n}` });
  }

  if (end < safeTotal) {
    items.push({ type: "ellipsis", key: "end-ellipsis" });
  }

  return { safePage, safeTotal, items };
}

export default function Pagination({
  page,
  totalPages,
  onPageChange,
  disabled = false,
  dir = "ltr",
  windowSize = 8,
  className = "",
}) {
  const { safePage, safeTotal, items } = useMemo(
    () => buildPageItems(page, totalPages, windowSize),
    [page, totalPages, windowSize]
  );

  if (safeTotal <= 1) return null;

  const isRtl = dir === "rtl";
  const canPrev = safePage > 1;
  const canNext = safePage < safeTotal;

  return (
    <div className={`pagination-wrap ${className}`.trim()}>
      <nav className="pagination" aria-label="Pagination">
        <button
          type="button"
          className="pagination__nav"
          onClick={() => onPageChange?.(safePage - 1)}
          disabled={disabled || !canPrev}
          aria-label="Previous page"
        >
          <i
            className={`fa-solid ${
              isRtl ? "fa-chevron-right" : "fa-chevron-left"
            }`}
            aria-hidden="true"
          ></i>
        </button>

        {items.map((item) => {
          if (item.type === "ellipsis") {
            return (
              <span key={item.key} className="pagination__ellipsis" aria-hidden>
                …
              </span>
            );
          }

          const isActive = item.value === safePage;
          return (
            <button
              key={item.key}
              type="button"
              className={`pagination__page ${
                isActive ? "is-active" : ""
              }`.trim()}
              onClick={() => onPageChange?.(item.value)}
              disabled={disabled}
              aria-current={isActive ? "page" : undefined}
            >
              {item.value}
            </button>
          );
        })}

        <button
          type="button"
          className="pagination__nav"
          onClick={() => onPageChange?.(safePage + 1)}
          disabled={disabled || !canNext}
          aria-label="Next page"
        >
          <i
            className={`fa-solid ${
              isRtl ? "fa-chevron-left" : "fa-chevron-right"
            }`}
            aria-hidden="true"
          ></i>
        </button>
      </nav>
    </div>
  );
}
